import parseSRT from 'parse-srt';
import stripTags from './strip-tags-service';
import SubtitleService from './subtitle-service';
import TimeFormatService from './time-format-service';
import _ from 'lodash';

// PHP's trim() strips only these characters. String#trim() also strips NBSP, BOM, etc.,
// which would let a line count as blank here that the API reads as text.
// eslint-disable-next-line no-control-regex
const phpTrim = (value) => value.replace(/^[ \t\n\r\0\x0B]+|[ \t\n\r\0\x0B]+$/g, '');

const SRTService = {
	// Mirrors the API parser (api/media/Srt.php). The API only parses the SRT after the
	// asset has been saved, so a file it rejects fails the save with a 500 - catch it on upload.
	validate: (data) => {
		const lines = data.split('\n');
		if(phpTrim(lines[lines.length - 1]) !== '') {
			lines.push('');
		}

		// The API reads each cue as a number line, a timecode line, then text up to a blank line
		let state = 'number';
		let timecode = '';
		let timecodeLine = 0;
		let cues = 0;

		lines.forEach((line, index) => {
			switch(state) {
				case 'number':
					state = 'timecode';
					break;
				case 'timecode':
					timecode = phpTrim(line);
					timecodeLine = index + 1;
					state = 'text';
					break;
				case 'text':
					if(phpTrim(line) === '') {
						if(timecode.split(' --> ').length < 2) {
							throw new Error(`Malformed SRT file: line ${timecodeLine} should be a timecode `
								+ '(e.g. "00:00:01,000 --> 00:00:02,000"). Check for missing or extra blank lines.');
						}
						cues++;
						state = 'number';
					}
					break;
			}
		});

		// The API cannot save a transcription without at least one cue
		if(!cues) {
			throw new Error('Malformed SRT file: no subtitles found');
		}
	},

	parse: (data) => {
		// check for malformed times
		const lines = data.split('\n');
		lines.forEach((line) => {
			if(line.includes(' --> ')) {
				const range = line.split(' --> ');
				if(!TimeFormatService.isValidTime(range[0]) || !TimeFormatService.isValidTime(range[1])) {
					throw new Error('Malformed SRT file');
				}
			}
		});

		return parseSRT(data)
			.map(({ start, end, text }) => ({
				startTime: SubtitleService.offsetToTimecode(start),
				stopTime: SubtitleService.offsetToTimecode(end),
				text: text.replace(/<br \/>/, '\n')
			}));
	},

	export: (lines) => {
		let result = '';
		let i = 1;

		lines.forEach((line) => {
			if(line.text === undefined) {
				return;
			}

			line.text = line.text.replace(/&nbsp;/g, ' ');

			// A contenteditable line break only wraps the line AFTER the break in
			// a <div> - the first line is left bare (e.g. "line1<div>line2</div>").
			// Treating only the closing tag as a separator fuses that first pair
			// of lines together, so the opening tag has to introduce the break
			// instead; the closing tag is just discarded.
			let text = _.unescape(
				line.text
					.replace(/<br\s*\/?>/gi, '\n')
					.replace(/<div\b[^>]*>/gi, '\n')
					.replace(/<\/div>/gi, '')
					.replace(/\r\n/g, '\n')
					.replace(/\r/g, '\n')
			);

			// Remove any tags that may have made it into the translation (div tags seem to be the main problem).
			// <b> <i> <u> are the only two supported tags currently
			text = stripTags(text, '<b><i><u>').replace(/^\n+|\n+$/g, '');

			result += (i++) + '\n';
			result += TimeFormatService.startTime(line) + ' --> ' + TimeFormatService.stopTime(line) + '\n';
			result += text + '\n\n';
		});
		return result;
	}
};

export default SRTService;
